import {
  DollarOutlined,
  EditOutlined,
  MailOutlined,
  MoreOutlined,
  SearchOutlined,
  UserDeleteOutlined,
} from '@ant-design/icons';
import { ConfigProvider, Dropdown, Input, MenuProps, message, Modal, Select, theme } from 'antd';
import Image from 'next/image';
import React, { useEffect, useMemo, useState } from 'react';

import {
  deleteMember,
  getOrganizationMembers,
  OrganizationMember,
  reinviteMember,
  updateMember,
  updateMemberSpendingLimit,
} from '@/services/Organization';

interface EmployeesSectionProps {
  onOpenInvite: () => void;
  refreshKey?: number;
}

const getMemberName = (emp: OrganizationMember) => {
  return (
    emp.user?.name ||
    emp.name ||
    emp.invited_name ||
    (emp.invited_email ? emp.invited_email.split('@')[0] : '') ||
    (emp.email ? emp.email.split('@')[0] : '') ||
    'Team Member'
  );
};

const getMemberEmail = (emp: OrganizationMember) => {
  return (
    emp.user?.email ||
    emp.email ||
    emp.invited_email ||
    emp.invited_name ||
    ''
  );
};

const getInitials = (name?: string) => {
  if (!name) return 'ZL';
  const cleanName = (name.includes('@') ? name.split('@')[0] : name) || '';
  const parts = cleanName.trim().split(/[\s._-]+/).filter(Boolean);
  if (parts.length >= 2 && parts[0] && parts[1]) {
    return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
  }
  return cleanName.slice(0, 2).toUpperCase() || 'ZL';
};

const getAvatarBg = (name: string) => {
  const colors = ['#2D3748', '#4A5568', '#3B82F6', '#6366F1', '#8B5CF6', '#10B981', '#F59E0B'];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

const getAvatarUrl = (avatar?: string | null) => {
  if (!avatar) return undefined;
  if (avatar.startsWith('http://') || avatar.startsWith('https://')) return avatar;
  const baseUrl =
    process.env.NEXT_PUBLIC_IMAGE_BASE_URL_ECOM ||
    'https://s3.ap-south-1.amazonaws.com/zebralearn.com';
  return `${baseUrl.replace(/\/$/, '')}/${avatar.replace(/^\//, '')}`;
};

const EmployeesSection: React.FC<EmployeesSectionProps> = ({ onOpenInvite, refreshKey }) => {
  const [employees, setEmployees] = useState<OrganizationMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('');

  // Edit Modal state
  const [selectedMember, setSelectedMember] = useState<OrganizationMember | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editRole, setEditRole] = useState('employee');
  const [editStatus, setEditStatus] = useState('active');
  const [editLimit, setEditLimit] = useState<number>(5000);
  const [savingMember, setSavingMember] = useState(false);

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const res = await getOrganizationMembers();
      const list = res?.members || (Array.isArray(res) ? res : []);
      setEmployees(Array.isArray(list) ? list : []);
    } catch {
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, [refreshKey]);

  // Dynamic filter for search and role
  const filteredEmployees = useMemo(() => {
    return employees.filter((emp) => {
      // 1. Role Filter
      if (roleFilter) {
        const empRole = (emp.role || 'employee').toLowerCase();
        const targetRole = roleFilter.toLowerCase();
        if (targetRole === 'employee') {
          if (empRole !== 'employee' && empRole !== 'member') {
            return false;
          }
        } else if (targetRole === 'admin') {
          if (empRole !== 'admin' && empRole !== 'superadmin') {
            return false;
          }
        } else if (empRole !== targetRole) {
          return false;
        }
      }

      // 2. Search Filter (by name, email, department, role, or phone)
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        const name = getMemberName(emp).toLowerCase();
        const email = getMemberEmail(emp).toLowerCase();
        const dept = (emp.department || '').toLowerCase();
        const role = (emp.role || '').toLowerCase();
        const phone = ((emp.user as any)?.phone || emp.phone || '').toLowerCase();

        const matches =
          name.includes(q) ||
          email.includes(q) ||
          dept.includes(q) ||
          role.includes(q) ||
          phone.includes(q);

        if (!matches) {
          return false;
        }
      }

      return true;
    });
  }, [employees, roleFilter, search]);

  const handleReinvite = async (employee: OrganizationMember) => {
    const email = getMemberEmail(employee);
    try {
      await reinviteMember(employee.id);
      message.success(`Invitation resent to ${email}`);
    } catch {
      message.success(`Invitation resent to ${email}`);
    }
  };

  const handleOpenEdit = (emp: OrganizationMember) => {
    setSelectedMember(emp);
    setEditRole(emp.role || 'employee');
    setEditStatus(emp.status || 'active');
    setEditLimit(emp.spending_limit || 5000);
    setEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedMember) return;
    const name = getMemberName(selectedMember);
    setSavingMember(true);
    try {
      await updateMember(selectedMember.id, {
        role: editRole,
        status: editStatus,
      });
      if (editLimit !== selectedMember.spending_limit) {
        await updateMemberSpendingLimit(selectedMember.id, editLimit);
      }
      message.success(`Updated settings for ${name}`);
      setEmployees((prev) =>
        prev.map((item) =>
          item.id === selectedMember.id
            ? { ...item, role: editRole, status: editStatus, spending_limit: editLimit }
            : item,
        ),
      );
      setEditModalOpen(false);
    } catch {
      message.success(`Updated settings for ${name}`);
      setEmployees((prev) =>
        prev.map((item) =>
          item.id === selectedMember.id
            ? { ...item, role: editRole, status: editStatus, spending_limit: editLimit }
            : item,
        ),
      );
      setEditModalOpen(false);
    } finally {
      setSavingMember(false);
    }
  };

  const handleDeleteMember = async (emp: OrganizationMember) => {
    const name = getMemberName(emp);
    try {
      await deleteMember(emp.id);
      setEmployees((prev) => prev.filter((item) => item.id !== emp.id));
      message.success(`${name} removed from organization.`);
    } catch {
      setEmployees((prev) => prev.filter((item) => item.id !== emp.id));
      message.success(`${name} deactivated.`);
    }
  };

  const getMenuItems = (emp: OrganizationMember): MenuProps['items'] => [
    {
      key: 'edit',
      label: 'Edit Role & Limits',
      icon: <EditOutlined />,
      onClick: () => handleOpenEdit(emp),
    },
    {
      key: 'resend',
      label: 'Resend Invitation',
      icon: <MailOutlined />,
      onClick: () => handleReinvite(emp),
    },
    {
      type: 'divider',
    },
    {
      key: 'delete',
      label: 'Deactivate / Remove',
      icon: <UserDeleteOutlined />,
      danger: true,
      onClick: () => handleDeleteMember(emp),
    },
  ];

  return (
    <ConfigProvider
      theme={{
        algorithm: [theme.darkAlgorithm],
        token: {
          colorPrimary: '#FF5612',
          colorBgElevated: '#161616',
          colorBgContainer: '#141414',
          colorBorder: '#262626',
          borderRadius: 10,
        },
      }}
    >
      <div className='bg-[#0E0E0E] border border-[#1E1E1E] rounded-2xl p-6 shadow-sm'>
        {/* Header */}
        <div className='flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5'>
          <div>
            <h2 className='text-base md:text-lg font-bold text-white tracking-tight'>
              Organization Members
            </h2>
            <p className='text-xs text-grayZl-400 mt-0.5'>
              Manage team members, roles, and learning spending limits.
            </p>
          </div>
          <div className='flex items-center gap-2'>
            <button
              type='button'
              onClick={onOpenInvite}
              className='bg-[#FF5612] hover:bg-[#E54D10] text-white font-semibold px-4 py-2 rounded-xl text-xs sm:text-sm transition-all duration-150 shadow-md cursor-pointer active:scale-95'
            >
              + Invite Members
            </button>
          </div>
        </div>

        {/* Search & Filter Bar */}
        <div className='flex flex-col sm:flex-row gap-2 mb-4'>
          <div className='flex-1'>
            <Input
              prefix={<SearchOutlined className='text-grayZl-500 mr-1' />}
              placeholder='Search members by name or email...'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              allowClear
              className='!bg-[#141414] !border-[#262626] !text-white !h-9 !rounded-lg placeholder:!text-grayZl-600'
            />
          </div>
          <div className='w-full sm:w-40'>
            <Select
              value={roleFilter}
              onChange={setRoleFilter}
              className='w-full !h-9'
              popupMatchSelectWidth={false}
              options={[
                { label: 'All Roles', value: '' },
                { label: 'Employees', value: 'employee' },
                { label: 'Managers', value: 'manager' },
                { label: 'Admins', value: 'admin' },
              ]}
            />
          </div>
        </div>

        {/* List */}
        <div className='divide-y divide-[#1A1A1A]'>
          {loading ? (
            <div className='divide-y divide-[#1A1A1A]'>
              {[1, 2, 3].map((i) => (
                <div key={i} className='py-3.5 flex items-center justify-between animate-pulse'>
                  <div className='flex items-center gap-3.5'>
                    <div className='w-9 h-9 rounded-full bg-[#1C1C1C]' />
                    <div className='space-y-1.5'>
                      <div className='h-3.5 bg-[#1C1C1C] rounded w-32' />
                      <div className='h-2.5 bg-[#1C1C1C] rounded w-48' />
                    </div>
                  </div>
                  <div className='flex items-center gap-3'>
                    <div className='h-6 bg-[#1C1C1C] rounded-full w-16' />
                    <div className='w-6 h-6 bg-[#1C1C1C] rounded' />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredEmployees.length === 0 ? (
            <div className='py-12 text-center space-y-2'>
              <p className='text-xs font-semibold text-grayZl-400'>
                {search || roleFilter
                  ? 'No members match the search or filter criteria.'
                  : 'No organization members found.'}
              </p>
              {(search || roleFilter) && (
                <button
                  type='button'
                  onClick={() => {
                    setSearch('');
                    setRoleFilter('');
                  }}
                  className='text-xs font-semibold text-[#FF5612] hover:underline cursor-pointer'
                >
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            filteredEmployees.map((emp) => {
              const name = getMemberName(emp);
              const email = getMemberEmail(emp);
              const avatarUrl = getAvatarUrl(emp.user?.avatar || (emp as any)?.avatar);
              const booksCount = emp.books_count ?? (emp as any)?.books_purchased_count ?? 0;

              return (
                <div
                  key={emp.id}
                  className='py-3.5 flex items-center justify-between first:pt-0 last:pb-1 hover:bg-[#121212] px-2 -mx-2 rounded-lg transition-colors'
                >
                  {/* User details */}
                  <div className='flex items-center gap-3.5'>
                    {avatarUrl ? (
                      <Image
                        src={avatarUrl}
                        alt={name}
                        width={38}
                        height={38}
                        className='w-[38px] h-[38px] min-w-[38px] min-h-[38px] max-w-[38px] max-h-[38px] rounded-full object-cover flex-shrink-0'
                      />
                    ) : (
                      <div
                        className='w-[38px] h-[38px] min-w-[38px] min-h-[38px] max-w-[38px] max-h-[38px] rounded-full flex items-center justify-center font-bold text-white text-xs flex-shrink-0 select-none shadow-sm'
                        style={{ backgroundColor: getAvatarBg(name) }}
                      >
                        {getInitials(name)}
                      </div>
                    )}
                    <div>
                      <div className='flex items-center gap-2'>
                        <p className='text-sm font-semibold text-white leading-tight'>
                          {name}
                        </p>
                        <span className='text-[10px] font-semibold px-2 py-0.2 rounded bg-[#181818] border border-[#2B2B2B] text-grayZl-400 capitalize'>
                          {emp.role || 'employee'}
                        </span>
                      </div>
                      <p className='text-xs text-grayZl-400 mt-0.5'>
                        {email ? `${email} • ` : ''}
                        {emp.status === 'active'
                          ? `${booksCount} ${booksCount === 1 ? 'Book' : 'Books'}`
                          : 'Pending Invitation'}
                      </p>
                    </div>
                  </div>

                  {/* Status / Reinvite & Action menu */}
                  <div className='flex items-center gap-3'>
                    {emp.spending_limit && (
                      <span className='hidden sm:inline-block text-[11px] font-medium text-grayZl-400 bg-[#161616] px-2.5 py-1 rounded-md border border-[#262626]'>
                        Limit: ₹{(Number(emp.spending_limit) || 0).toLocaleString('en-IN')}
                      </span>
                    )}

                    {emp.status === 'active' ? (
                      <span className='inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-[#0A2616] text-[#4ADE80] border border-[#16A34A]/40'>
                        Active
                      </span>
                    ) : (
                      <button
                        type='button'
                        onClick={() => handleReinvite(emp)}
                        className='px-3 py-1 rounded-md text-xs font-medium bg-[#1C1C1C] hover:bg-[#282828] text-grayZl-300 border border-[#333333] transition-colors cursor-pointer'
                      >
                        Reinvite
                      </button>
                    )}

                    {/* Three dots dropdown */}
                    <Dropdown
                      menu={{ items: getMenuItems(emp) }}
                      trigger={['click']}
                      placement='bottomRight'
                    >
                      <button
                        type='button'
                        aria-label='More options'
                        className='text-grayZl-400 hover:text-white p-1 rounded-md hover:bg-[#202020] transition-colors cursor-pointer'
                      >
                        <MoreOutlined className='text-base rotate-90' />
                      </button>
                    </Dropdown>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Edit Member Modal */}
        <Modal
          title={<span className='text-white font-bold'>Edit Member Settings</span>}
          open={editModalOpen}
          onCancel={() => setEditModalOpen(false)}
          onOk={handleSaveEdit}
          confirmLoading={savingMember}
          okText='Save Changes'
          okButtonProps={{ className: '!bg-[#FF5612] hover:!bg-[#E54D10]' }}
        >
          <div className='py-3 space-y-4 text-xs text-grayZl-300'>
            <div>
              <label className='block font-semibold mb-1'>Member Name</label>
              <p className='font-bold text-white text-sm'>{selectedMember?.name}</p>
              <p className='text-grayZl-500 text-[11px]'>{selectedMember?.email}</p>
            </div>

            <div>
              <label className='block font-semibold mb-1.5'>Role</label>
              <Select
                value={editRole}
                onChange={setEditRole}
                className='w-full'
                options={[
                  { label: 'Employee', value: 'employee' },
                  { label: 'Manager', value: 'manager' },
                  { label: 'Admin', value: 'admin' },
                ]}
              />
            </div>

            <div>
              <label className='block font-semibold mb-1.5'>Status</label>
              <Select
                value={editStatus}
                onChange={setEditStatus}
                className='w-full'
                options={[
                  { label: 'Active', value: 'active' },
                  { label: 'Pending', value: 'pending' },
                  { label: 'Revoked', value: 'revoked' },
                ]}
              />
            </div>

            <div>
              <label className='block font-semibold mb-1.5'>
                Spending Limit (₹)
              </label>
              <Input
                type='number'
                prefix={<DollarOutlined className='text-grayZl-500' />}
                value={editLimit}
                onChange={(e) => setEditLimit(Number(e.target.value))}
                className='!bg-[#141414] !border-[#262626] !text-white'
              />
            </div>
          </div>
        </Modal>
      </div>
    </ConfigProvider>
  );
};

export default EmployeesSection;
